import { SEO } from '../../components/seo/SEO';
import { getBaseUrl } from '../../lib/utils';

interface Tool {
  name: string;
  category: string;
  desc: string;
  link: string;
  icon: string;
  tagColor: string;
  tagBg: string;
  tagBorder: string;
  badge?: string;
}

const tools: Tool[] = [
  {
    name: 'Cursor',
    category: 'AI開発',
    desc: 'AIコペアプログラミングエディタ。Claude / GPT-4oと組み合わせて使うと、仕様を話すだけで実装が進む。個人開発の主力ツール。',
    link: 'https://cursor.sh',
    icon: 'ri-code-s-slash-line',
    tagColor: '#C96F3B', tagBg: '#FEF3E8', tagBorder: '#E8C4A0',
    badge: '愛用中',
  },
  {
    name: 'Claude',
    category: 'AI',
    desc: 'Anthropicの大規模言語モデル。長文ドキュメントの読み込み・仕様書生成・プロンプト設計に使いやすい。Cursorのエンジンとしても活用。',
    link: 'https://claude.ai',
    icon: 'ri-robot-line',
    tagColor: '#C96F3B', tagBg: '#FEF3E8', tagBorder: '#E8C4A0',
    badge: '愛用中',
  },
  {
    name: 'Readdy.ai',
    category: 'AI開発',
    desc: 'このサイト自体をReaddy.aiで構築した。フロントエンドのコード生成・ページ制作・デプロイまで一貫して対応できる。',
    link: 'https://readdy.ai',
    icon: 'ri-layout-masonry-line',
    tagColor: '#C96F3B', tagBg: '#FEF3E8', tagBorder: '#E8C4A0',
    badge: '本サイト使用',
  },
  {
    name: 'Notion',
    category: '業務効率化',
    desc: 'プロンプトライブラリ・記事管理・仕様書保管など、テキストベースの情報管理はほぼNotionに集約している。',
    link: 'https://notion.so',
    icon: 'ri-file-list-2-line',
    tagColor: '#5E7C6B', tagBg: '#EFF5F2', tagBorder: '#A8C4B8',
  },
  {
    name: 'Raycast',
    category: 'PCツール',
    desc: 'Macのランチャー。ファイル検索・クリップボード履歴・スニペット展開が全部一箇所にまとまっている。Spotlightより圧倒的に速い。',
    link: 'https://raycast.com',
    icon: 'ri-search-2-line',
    tagColor: '#6F8FAF', tagBg: '#EEF3F8', tagBorder: '#ACC3D8',
    badge: '効率爆上がり',
  },
  {
    name: 'Supabase',
    category: 'バックエンド',
    desc: 'PostgreSQL ベースのBaaS。個人開発の無料枠で十分な機能を提供。記事管理・ユーザー認証に使用する予定。',
    link: 'https://supabase.com',
    icon: 'ri-database-2-line',
    tagColor: '#5E7C6B', tagBg: '#EFF5F2', tagBorder: '#A8C4B8',
  },
  {
    name: 'Linear',
    category: '業務効率化',
    desc: 'プロジェクト管理ツール。個人開発でもタスク管理・バックログ整理に使うと思考が整理しやすい。UIが気持ちいい。',
    link: 'https://linear.app',
    icon: 'ri-task-line',
    tagColor: '#5E7C6B', tagBg: '#EFF5F2', tagBorder: '#A8C4B8',
  },
  {
    name: 'Warp',
    category: 'PCツール',
    desc: 'AI補完内蔵のモダンターミナル。コマンドの説明・補完・実行履歴が整理されていて、ターミナルが苦手な人でも使いやすい。',
    link: 'https://warp.dev',
    icon: 'ri-terminal-box-line',
    tagColor: '#6F8FAF', tagBg: '#EEF3F8', tagBorder: '#ACC3D8',
  },
];

const toolCategories = ['すべて', 'AI開発', 'AI', '業務効率化', 'PCツール', 'バックエンド'];

export default function ToolsPage() {
  const baseUrl = getBaseUrl();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'ツール・リソース — つくってなんぼ',
    url: `${baseUrl}/tools`,
    description: '実際に使って役立ったツール・サービスの一覧。紹介ではなく、実際に使った結果を記事にする方針でまとめています。',
  };

  return (
    <>
      <SEO
        title="ツール・リソース — つくってなんぼ | AI活用・個人開発・業務効率化"
        description="実際に使って役立ったツール・サービスの一覧。Cursor・Claude・Notion・Raycastなど、AI活用・個人開発・業務効率化で本当に使えたツールをまとめています。"
        canonical="/tools"
        jsonLd={jsonLd}
      />

      <main className="min-h-screen bg-[#F6F1E8]">
        {/* Header */}
        <section className="pt-24 sm:pt-32 pb-10 sm:pb-14 bg-[#F6F1E8]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold tracking-widest uppercase text-[#C96F3B]">ツール・リソース</span>
              <div className="w-8 h-0.5 bg-[#C96F3B] mt-1.5 mb-4"></div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-[#3E332A] leading-tight mb-4"
                style={{ fontFamily: "'M PLUS 1p', 'Noto Sans JP', sans-serif" }}>
                実際に使って<br />役立ったツール
              </h1>
              <p className="text-base text-[#7A6A5A] leading-relaxed">
                並べるためではなく、実際に実装や運用で使った結果を書いています。
                紹介記事へのリンクがあるものは詳細も読めます。
              </p>
            </div>
          </div>
        </section>

        {/* Tools grid */}
        <section className="pb-16 sm:pb-20 bg-[#F6F1E8]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category filter chips */}
            <div className="flex flex-wrap gap-2 mb-8">
              {toolCategories.map((cat) => (
                <button
                  key={cat}
                  className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                    cat === 'すべて'
                      ? 'bg-[#C96F3B] text-white border-[#C96F3B]'
                      : 'bg-[#FFFDF8] text-[#7A6A5A] border-[#C7B8A3] hover:border-[#C96F3B] hover:text-[#C96F3B]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools.map((tool) => (
                <a
                  key={tool.name}
                  href={tool.link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group flex flex-col p-4 sm:p-5 rounded-lg border border-[#C7B8A3] bg-[#FFFDF8] hover:border-[#C96F3B]/50 transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
                  style={{ boxShadow: '0 1px 4px rgba(62,51,42,0.06)' }}
                >
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 flex items-center justify-center rounded"
                        style={{ backgroundColor: tool.tagBg, border: `1px solid ${tool.tagBorder}` }}>
                        <i className={`${tool.icon} text-base`} style={{ color: tool.tagColor }}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-[#3E332A]"
                            style={{ fontFamily: "'M PLUS 1p', 'Noto Sans JP', sans-serif" }}>
                            {tool.name}
                          </span>
                          {tool.badge && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-[#FEF3E8] text-[#C96F3B] border border-[#E8C4A0] font-medium whitespace-nowrap">
                              {tool.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-xs"
                          style={{ color: tool.tagColor }}>
                          {tool.category}
                        </span>
                      </div>
                    </div>
                    <i className="ri-external-link-line text-sm text-[#C7B8A3] group-hover:text-[#C96F3B] transition-colors flex-shrink-0 mt-1"></i>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-[#7A6A5A] leading-relaxed line-clamp-3">
                    {tool.desc}
                  </p>
                </a>
              ))}
            </div>

            {/* Note */}
            <div className="mt-10 rounded-lg border border-[#C7B8A3] bg-[#E8DCC8] p-4 sm:p-5">
              <p className="text-sm text-[#7A6A5A] leading-relaxed">
                <i className="ri-information-line mr-1.5 text-[#C96F3B]"></i>
                このページは随時更新中です。使い続けているツール・乗り換えたツール・辞めたツールも正直に記録していきます。
                各ツールの詳しい使い方は記事でも発信しています。
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
